import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import authInstance from './../../utilities/axios/authInstance';
import { handleError } from './../../utilities/errorHandler';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import PageTitle from './../shared/PageTitle';
import Message from './../shared/Message';
import ReportSelector from './ReportSelector';
import { FaArrowLeft, FaDownload, FaEye } from 'react-icons/fa6';
import ReportFilterFactory from './ReportFilterFactory';
import ReportPreviewTable from './ReportPreviewTable';

const ReportPage = () => {
    const [step, setStep] = useState(1);
    const [generating, setGenerating] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [message, setMessage] = useState({ show: false, text: '', variant: ''});
    const [previewData, setPreviewData] = useState(null);

    const {control, register, handleSubmit, formState: {errors}, watch, reset, setValue, trigger} = useForm({
        defaultValues: {
            report_type: null,
            filters: {},
            reportFormat: 'pdf'
        }
    });

    const selectedReportOption = watch('report_type');

    // Auto-advance to Step 2 only if we are currently on Step 1
    useEffect(() => {
      if (selectedReportOption && step === 1) {
        setStep(2);
      }
    }, [selectedReportOption, step]);

    // Navigate back to selection and clear the state 
    const handleBackToStep1= () => {
      setValue('report_type', null);
      setStep(1);
    }

    // Step 2 -> Step 3: Validate and Fetch Preview 
    const onPreview = async () => {
      // Trigger validation only for fields inside the 'filters' object 
      const isValid = await trigger('filters');

      if (isValid) {
        setPreviewing(true);
        const formData = watch();
        console.log(formData);
        try {
          const response = await authInstance.post('reports/preview', {
            report_id: formData.report_type.value.id,
            filters: formData.filters
          });

          setPreviewData(response.data);
          setStep(3);
        } catch (error) {
          handleError(error, setMessage);
        } finally {
          setPreviewing(false);
        }
      } 
    };

    // Step 3 -> Step 4: Final Download 
    const onGenerateReport = async (data) => {
      setGenerating(true);
      try {
        const response = await authInstance.post('reports/export', {
          report_id: data.report_type.value.id,
          filters: data.filters,
          reportFormat: data.reportFormat
        }, {responseType: 'blob'});

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href =url;
        link.setAttribute('download', `${data.report_type.label}.${data.reportFormat === 'excel' ? 'xlsx' : data.reportFormat}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        handleError(error, setMessage);
      } finally {
        setGenerating(false);
      }
    }

    const resetWorkflow = () => {
      reset();
      setStep(1);
      setPreviewData(null);
    }
  return (
    <Container fluid className='py-4'>
      <PageTitle title="Reports Center" />
      {message.show && 
        <Message
          variant={message.variant}
          message={message.text}
        />
      }

      <Card className='shadow-sm border-0'>
        <Card.Body className='py-4'>
          <Form onSubmit={handleSubmit(onGenerateReport)}>

            {/** STEP 1: SELECT REPORT */}
            {step === 1 && <ReportSelector control={control} name="report_type"rules={{ required: 'Select report'}} />}

            {/** STEP 2: CONFIGURE FILTERS */}
            {step === 2 && (
              <div className="animate__animated animate__fadeIn">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Button type='button' variant='link' className='p-0 text-decoration-none' onClick={handleBackToStep1}>
                    <FaArrowLeft className='me-2' /> Back to Report Selection 
                  </Button>
                  <h5 className="mb-0 fw-bold">{selectedReportOption?.label}</h5>
                </div>

                <ReportFilterFactory control={control} register={register} errors={errors} />

                <div className="text-end mt-4">
                  <Button type='button' variant='primary' size='lg' onClick={onPreview} disabled={previewing}>
                    {previewing ? <Spinner size='sm' /> : <><FaEye className='me-2' /> Preview Data</>}
                  </Button>
                </div>
              </div>
            )}

            {/** STEP 3 & 4: PREVIEW AND DOWNLOAD */}
            {step === 3 && (
              <div className="animate__animated aninamte__fadeIn">
                <div className="d-flex justify-content-betweeh aling-items-center mb-4">
                  <Button type='button' variant='link' className='p-0 text-decoration-none' onClick={() => setStep(2)}>
                    <FaArrowLeft className='me-2' /> Edit Filters
                  </Button>
                  <h5 className="mb-0 fw-bold">Data Preview</h5>
                </div>

                <div className="mb-4 border rounded bg-white shadow-sm p-3">
                  <ReportPreviewTable data={previewData} />
                </div>

                <Row className='align-items-center bg-light p-4 rounded-3 g-3'>
                  <Col md={6}>
                    <Form.Label className='fw-bold mb-3 d-block'>Select Export Format</Form.Label>
                    <div className="d-flex gap-4">
                      {['pdf', 'excel','csv'].map((fmt) => (
                        <Form.Check
                          key={fmt}
                          type='radio'
                          id={`format-${fmt}`}
                          label={fmt.toUpperCase()}
                          value={fmt}
                          {...register('reportFormat')}
                          className='fw-medium'
                        />
                      ))}
                    </div>
                  </Col>

                  <Col md={6}>
                      <Button type='submit' variant='success' size='lg' className='px-5' disabled={generating}>
                        {generating ? <Spinner size='sm' /> : <><FaDownload className='me-2' />Download Report</>}
                      </Button>
                  </Col>
                </Row>

                <div className="mt-4 text-center">
                  <Button variant='link' onClick={resetWorkflow} className='text-danger text-decoration-none small'>
                    Cancel and Start Over 
                  </Button>
                </div>

              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default ReportPage